// Новые типы данных согласно спецификации

export interface ResumeData {
  id?: string;
  lang: 'ru' | 'en';
  position: string;
  photoUrl?: string;
  photo?: string; // Base64 encoded photo
  name: {
    last: string;
    first: string;
    middle?: string;
  };
  birthDate: string; // YYYY-MM-DD
  location: {
    city: string;
  };
  meta?: {
    citizenship?: string;
    relocation?: boolean;
    travelReady?: boolean;
    employment?: string;
    schedule?: string;
  };
  contacts: {
    email: string;
    phones: string[];
  };
  experience: Array<{
    title: string;
    company: string;
    dateFrom: string; // YYYY-MM
    dateTo?: string; // YYYY-MM
    description?: string;
    incomplete?: boolean;
  }>;
  education: Array<{
    org: string;
    faculty?: string;
    degree: string;
    specialty: string;
    graduationYear: number;
    form?: string;
    incomplete?: boolean;
  }>;
  courses: Array<{
    organization: string;
    name: string;
    year: number;
    description?: string;
  }>;
  hobbies: Array<{
    name: string;
    description?: string;
  }>;
  languages: Array<{
    name: string;
    level: string;
  }>;
  skills: string[];
  about?: string;
  extra?: {
    softSkills?: string[];
    hobbies?: string;
    references?: Array<{ name: string; contact: string }>;
    publications?: Array<{ title: string; link: string }>;
    other?: string;
  };
  template: {
    id: string;
    accent: string;
    withPhoto: boolean;
  };
  sharing?: {
    public: boolean;
    password?: string;
  };
  /** Есть у резюме, созданного из консультанта (CreateProjectFlow). */
  projectId?: number | null;
}

export interface Template {
  id: string;
  name: string;
  version: number;
  previewImage: string;
  page: {
    size: string;
    margins: {
      top: string;
      right: string;
      bottom: string;
      left: string;
    };
  };
  colors: {
    primary: string;
    secondary: string;
    text: string;
    muted: string;
    bg: string;
    sidebarBg?: string;
    sidebarText?: string;
    decorativeLine?: string;
    border?: string;
    accent?: string;
  };
  fonts: {
    baseFamily: string[];
    scale: {
      base: number;
      h1: number;
      h2: number;
      h3: number;
      h4: number;
      h5: number;
      h6: number;
    };
    allowOverride?: boolean;
  };
  layout: {
    type: 'single-column' | 'two-column';
    sidebarWidth?: string;
    order: string[];
  };
  components: {
    profile: string;
    contacts: string;
    experience: string;
    education: string;
    skills: string;
    languages: string;
    summary: string;
  };
  translations: {
    [lang: string]: {
      [key: string]: string;
    };
  };
}

export interface UserOverrides {
  colors?: Partial<Template["colors"]>;
  fontFamily?: string;
  fontSize?: number;
  fonts?: {
    baseFamily?: string[];
    scale?: number;
  };
  layout?: Partial<Template["layout"]>;
}

export const DEFAULT_TEMPLATES: Template[] = [
  {
    id: 'template-01',
    name: 'Шаблон 01',
    version: 1,
    previewImage: '/templates/template-01.png',
    page: {
      size: 'A4',
      margins: {
        top: '0mm',
        right: '0mm',
        bottom: '0mm',
        left: '0mm'
      }
    },
    colors: {
      primary: '#111827',
      secondary: '#F3F4F6',
      text: '#111827',
      muted: '#6B7280',
      bg: '#ffffff',
      decorativeLine: '#F3F4F6',
      accent: '#AFD0E1' // Дефолтный голубой акцент
    },
    fonts: {
      baseFamily: ['Inter', 'system-ui', 'sans-serif'],
      scale: {
        base: 10,
        h1: 32,
        h2: 14,
        h3: 12,
        h4: 10,
        h5: 10,
        h6: 10
      },
      allowOverride: true
    },
    layout: {
      type: 'single-column',
      order: ['profile', 'contacts', 'summary', 'experience', 'education', 'skills', 'languages', 'courses', 'hobbies']
    },
    components: {
      profile: 'ProfileHeaderDecorative',
      contacts: 'ContactTable',
      experience: 'ExperienceListDecorative',
      education: 'EducationListDecorative',
      skills: 'SkillListDecorative',
      languages: 'LangLevelSimple',
      summary: 'ParagraphDecorative'
    },
    translations: {
      'ru-RU': {
        skills: 'НАВЫКИ',
        education: 'ОБРАЗОВАНИЕ',
        experience: 'ОПЫТ РАБОТЫ',
        languages: 'ЯЗЫКИ',
        courses: 'КУРСЫ',
        hobbies: 'УВЛЕЧЕНИЯ',
        summary: 'О СЕБЕ',
        contacts: 'КОНТАКТЫ'
      },
      'en-US': {
        skills: 'SKILLS',
        education: 'EDUCATION',
        experience: 'EXPERIENCE',
        languages: 'LANGUAGES',
        courses: 'COURSES',
        hobbies: 'HOBBIES',
        summary: 'SUMMARY',
        contacts: 'CONTACTS'
      }
    }
  },
  {
    id: 'template-02',
    name: 'Шаблон 02',
    version: 1,
    previewImage: '/templates/template-02.png',
    page: {
      size: 'A4',
      margins: {
        top: '0mm',
        right: '0mm',
        bottom: '0mm',
        left: '0mm'
      }
    },
    colors: {
      primary: '#111827',
      secondary: '#E5E7EB',
      text: '#111827',
      muted: '#6B7280',
      bg: '#ffffff',
      decorativeLine: '#E5E7EB',
      accent: '#AFD0E1'
    },
    fonts: {
      baseFamily: ['Inter', 'system-ui', 'sans-serif'],
      scale: {
        base: 10,
        h1: 32,
        h2: 14,
        h3: 12,
        h4: 10,
        h5: 10,
        h6: 10
      },
      allowOverride: true
    },
    layout: {
      type: 'single-column',
      order: ['profile', 'contacts', 'summary', 'experience', 'education', 'skills', 'languages', 'courses', 'hobbies']
    },
    components: {
      profile: 'ProfileHeaderPhoto',
      contacts: 'ContactListIcons',
      experience: 'ExperienceListPhoto',
      education: 'EducationListPhoto',
      skills: 'SkillListPhoto',
      languages: 'LangLevelSimple',
      summary: 'ParagraphPhoto'
    },
    translations: {
      'ru-RU': {
        skills: 'НАВЫКИ',
        education: 'ОБРАЗОВАНИЕ',
        experience: 'ОПЫТ РАБОТЫ',
        languages: 'ЯЗЫКИ',
        courses: 'КУРСЫ',
        hobbies: 'УВЛЕЧЕНИЯ',
        summary: 'О СЕБЕ',
        contacts: 'КОНТАКТЫ'
      },
      'en-US': {
        skills: 'SKILLS',
        education: 'EDUCATION',
        experience: 'EXPERIENCE',
        languages: 'LANGUAGES',
        courses: 'COURSES',
        hobbies: 'HOBBIES',
        summary: 'SUMMARY',
        contacts: 'CONTACTS'
      }
    }
  },
  {
    id: 'template-03',
    name: 'Шаблон 03',
    version: 1,
    previewImage: '/templates/template-03.png',
    page: {
      size: 'A4',
      margins: {
        top: '0mm',
        right: '0mm',
        bottom: '0mm',
        left: '0mm'
      }
    },
    colors: {
      primary: '#111827',
      secondary: '#374151',
      text: '#111827',
      muted: '#6B7280',
      bg: '#ffffff',
      sidebarBg: '#F9FAFB',
      sidebarText: '#111827',
      border: '#E5E7EB',
      accent: '#AFD0E1'
    },
    fonts: {
      baseFamily: ['Inter', 'system-ui', 'sans-serif'],
      scale: {
        base: 10,
        h1: 32,
        h2: 14,
        h3: 12,
        h4: 10,
        h5: 10,
        h6: 10
      },
      allowOverride: true
    },
    layout: {
      type: 'two-column',
      sidebarWidth: '240px',
      order: ['profile', 'contacts', 'languages', 'skills', 'experience', 'education', 'courses', 'hobbies', 'summary']
    },
    components: {
      profile: 'ProfileSidebar',
      contacts: 'ContactListSidebar',
      experience: 'ExperienceListSidebar',
      education: 'EducationListSidebar',
      skills: 'SkillListSidebar',
      languages: 'LangLevelSidebar',
      summary: 'ParagraphSidebar'
    },
    translations: {
      'ru-RU': {
        skills: 'навыки',
        education: 'Образование',
        experience: 'Опыт работы',
        languages: 'Языки',
        courses: 'Курсы',
        hobbies: 'Увлечения',
        summary: 'О себе',
        contacts: 'Контакты'
      },
      'en-US': {
        skills: 'Skills',
        education: 'Education',
        experience: 'Experience',
        languages: 'Languages',
        courses: 'Courses',
        hobbies: 'Hobbies',
        summary: 'Summary',
        contacts: 'Contacts'
      }
    }
  }
];

export const DEFAULT_RESUME_DATA: ResumeData = {
  id: '',
  lang: 'ru',
  position: '',
  photoUrl: '',
  name: {
    last: '',
    first: '',
    middle: ''
  },
  birthDate: '',
  location: {
    city: ''
  },
  meta: {
    citizenship: '',
    relocation: false,
    travelReady: false,
    employment: '',
    schedule: ''
  },
  contacts: {
    email: '',
    phones: []
  },
  experience: [],
  education: [],
  courses: [],
  hobbies: [],
  languages: [],
  skills: [],
  about: '',
  extra: {
    softSkills: [],
    hobbies: '',
    references: [],
    publications: [],
    other: ''
  },
  template: {
    id: 'template-01',
    accent: '#AFD0E1',
    withPhoto: true
  },
  sharing: {
    public: false
  }
};
